import { randomElement, randomString, updateTables } from "./utils";

/**
 * Given a form and its subform, a MoveWrapper function converts a move on the subform to a move on the superform.
 * It must guarantee that the returned move matches the form.
 *
 * @callback MoveWrapper
 * @param v - a move on a subform
 * @return a move on a superform
 */

/**
 * A visitor is a function that is invoked on each subform.
 * It implicitly assumes that there is a form that is being traversed.
 * It must return either `undefined` if no decision is made, or a traversing result.
 * A traversing result is one of the following:
 * - an instruction to skip the subform and its proper subforms since no move shall be made,
 * - a move if there is a move to play right now,
 * - a pending move if the subform triggers an evaluation (sync or async) with some data, and it may result in a move.
 *
 * Arguments of the visitor consist of the subform and three supplimentary functions to construct traversing results.
 *
 * @callback Visitor
 * @template A - a type of payload data (in case of PendingMove)
 * @param {Form} subform - a subform of the traversed form
 * @param {NoMove} skip - an instruction to skip
 * @param {MoveConstructor} move - a function to return a move
 * @param {PendingMoveConstructor<A>} pendingMove - a function to provide data for pending move
 * @return {undefined | TraverseResult<A>}
 */

/**
 * A function to construct a move for a subform that is being visited.
 * @callback MoveConstructor
 * @param {*} move - a move on the subform
 * @return {Move} a move as a traversing result
 */

/**
 * A function to construct a pending move for a subform that is being visited.
 * @callback PendingMoveConstructor
 * @template A - a type of payload data
 * @param {A} data - payload data that can be used in the further evaluation
 * @return {PendingMove} a pending move as a traversing result
 */

/**
 * A set of callbacks to process a TraverseResult instance.
 *
 * Given a traversing result, it can be one of three possible subclasses.
 * That is, one can find out the subclass of the traversing result and apply one of given callbacks depending on the subclass.
 * Each callback defines a value that will be returned if the traversing result is an instance of the particular subclass.
 * Each callback must return a value of the same type.
 * Arguments of each callback are properties of the traversing result. Types of these properties depend on the subclass.
 * A type of all callbacks to process a traversing result is `TraverseResultCallbacks`.
 *
 * @typedef {object} TraverseResultCallbacks
 * @template A - a type of payload data (in case of PendingMove)
 * @template R - a type of value to return
 * @property {NoMoveCallback<R>} onNoMove - a callback called when the result is an instance of NoMove
 * @property {MoveCallback<R>} onMove - a callback called when the result is an instance of Move
 * @property {PendingMoveCallback<A, R>} onPendingMove - a callback called when the result is an instance of PendingMove
 */

/**
 * @callback NoMoveCallback
 * @template R
 * @return {R}
 */

/**
 * @callback MoveCallback
 * @template R
 * @param {*} move - a move
 * @return {R} a value depending on the move
 */

/**
 * @callback PendingMoveCallback
 * @template A
 * @template R
 * @param {A} data - payload data
 * @param {Form} subform - a subform
 * @param {MoveWrapper} moveWrapper - a function to convert a move (see PendingMove)
 * @return {R}
 */

/**
 * Abstract class of a traversing result.
 * @abstract
 * @template A - a type of payload data
 */
class TraverseResult {
  /**
   * An induction, or pattern matching.
   * Each child class must implement this method.
   *
   * @template R - a type of result
   * @param {TraverseResultCallbacks<A, R>} callbacks - a dictionary of callbacks, one of which is going to be called
   * @return {R} a value that returns one of callbacks
   * @abstract
   */
  ind(callbacks) {
    throw new Error("Not implemented");
  }
}

/**
 * A case when no move is available or shall be played.
 * An instance of NoMove class serves as an instruction to skip the subform that is under consideration.
 */
class NoMove extends TraverseResult {
  /**
   * @template R
   * @param {TraverseResultCallbacks<A, R>} callbacks
   * @return {R}
   */
  ind({ onNoMove }) {
    return onNoMove();
  }
}

/**
 * An instance of NoMove.
 */
const NO_MOVE = new NoMove();

/**
 * An instruction to return a move given explicitly.
 */
class Move extends TraverseResult {
  /**
   * @param {*} move - a move on a subform that is being visited
   */
  constructor(move) {
    super();
    this.move = move;
  }

  /**
   * @template R
   * @param {TraverseResultCallbacks<A, R>} callbacks
   * @return {R}
   */
  ind({ onMove }) {
    return onMove(this.move);
  }
}

/**
 * A pending move.
 * It allows to return a data payload and construct a move later.
 * @template A - a type of data payload
 */
class PendingMove extends TraverseResult {
  /**
   * @param {A} data - data payload
   * @param {Form} subform - a subform the pending move is associated with
   * @param {MoveWrapper} moveWrapper - a move wrapper to convert the move on the subform to a move on the initial form
   */
  constructor(data, subform, moveWrapper) {
    super();
    this.data = data;
    this.subform = subform;
    this.moveWrapper = moveWrapper;
  }

  /**
   * @template R
   * @param {TraverseResultCallbacks<A, R>} callbacks
   * @return {R}
   */
  ind({ onPendingMove }) {
    return onPendingMove(this.data, this.subform, this.moveWrapper);
  }
}

/**
 * @typedef {object} TraverseOptions
 * @property {boolean} [randomSumForm=true] - if set, traverse all the branches of SumForm, SectionForm and other complex forms
 * @property {boolean} [randomInput=true] - if set, return random move on InputOption and InputString
 */

/**
 * Traverse a form with a visitor.
 *
 * The visitor is invoked each time when a subform is visited.
 * If the visitor returns a traversing result, it is converted to match the initial form.
 *
 * @template A
 * @param {Form} form - a form to traverse
 * @param {Visitor<A>} visitor - a visitor function
 * @param {TraverseOptions} [opts] - additional options
 * @return {null | Move | PendingMove<A>} a move on the form that is defined by the visitor, or null if no move is available
 */
export default function traverseForm(form, visitor, opts = {}) {
  const { randomSumForm = true, randomInput = true } = opts;
  const traverseSubform = (subform) => traverseForm(subform, visitor, opts);
  const v = visitor(form, NO_MOVE, (v) => new Move(v), (data) => new PendingMove(data, form, (v) => v));
  if (v !== undefined) {
    return v;
  }
  switch (form.type) {
    case "ActionButton":
    case "InputOption":
      return randomInput ? new Move(undefined) : NO_MOVE;
    case "InputString":
      return randomInput ? new Move(randomString()) : NO_MOVE;
    case "SumForm":
    case "SectionForm": {
      if (randomSumForm) {
        // Each subform provides one option, either a traversing result or null
        const subformOptions = form.subforms.map((subform, index) => {
          // return null if the subform must be skipped, or a traversing result otherwise
          return traverseSubform(subform).ind({
            onNoMove: () => null,
            onMove: (move) => new Move({ mark: index, value: move }),
            onPendingMove: (data, subform, wrapToSubform) => {
              const wrapToForm = (v) => ({ mark: index, value: wrapToSubform(v) });
              return new PendingMove(data, subform, wrapToForm);
            },
          });
        });
        // remove null options
        const options = subformOptions.filter(Boolean);
        // return random option if exists
        return options.length > 0 ? randomElement(options) : NO_MOVE;
      } else {
        // find the first subform that provides a traversing result to return
        for (const [key, subform] of Object.entries(form.subforms)) {
          // a key of form.subforms is a string that contains an index of the subform
          const index = +key;
          // the subform provides one option, either a traversing result or null
          const option = traverseSubform(subform).ind({
            onNoMove: () => null,
            onMove: (move) => new Move({ mark: index, value: move }),
            onPendingMove: (data, subform, wrapToSubform) => {
              const wrapToForm = (v) => ({ mark: index, value: wrapToSubform(v) });
              return new PendingMove(data, subform, wrapToForm);
            },
          });
          if (option !== null) {
            return option;
          }
        }
      }
      return NO_MOVE;
    }
    case "SumSubj":
      return traverseForm({ type: "SumForm", subforms: form.forms }, visitor, opts);
    case "LockedForm":
    case "TextForm":
    case "EmptyForm":
    case "HTMLElementForm":
    case "HTMLElementForm1":
    case "ListingForm":
    case "PlotGraphics":
    case "Histogram":
    case "ReadOnlyTable":
      return NO_MOVE;
    case "ListBox":
      return form.disabled || !randomInput ? NO_MOVE : new Move(Math.floor(Math.random() * form.items.length));
    case "TimeForm":
    case "MessageForm":
    case "WorkingArea":
    case "WorkingAreaUnformatted":
    case "Cell":
    case "FramedForm":
    case "LabeledForm":
    case "LabeledWithForm":
    case "HintForm":
    case "MathJaxed":
    case "MarkedForm":
      return traverseSubform(form.form);
    case "NestedForm":
      return traverseSubform(form.subform);
    case "ApplicationForm":
      return randomInput ? new Move(updateTables(form.datas)) : NO_MOVE;
    case "IfForm":
      return traverseSubform(form.subforms[0]);
    case "HeaderButton":
      return form.clickable && !form.locked && form.visible ? new Move({}) : NO_MOVE;
    case "UploadFile":
      return NO_MOVE; // TODO: implement
    case "EditableTable":
      return NO_MOVE; // TODO: implement
    case "cmd":
      return randomInput ? new Move({}) : NO_MOVE;
  }
  throw new Error(`Unknown form type "${form.type}". You might want to add this type to traverseForm by modifying its code.`);
}
